import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ImageUploader from '../../components/ImageUploader';
import DishDetail from '../../components/DishDetail';
import CartDetail from '../../components/CartDetail';
import MenuList from '../../components/MenuList';
import AddToCartAnimation from '../../components/AddToCartAnimation';
import LoadingOverlay from '../../components/LoadingOverlay';
import PageHeader from '../../components/PageHeader';
import OrderSheet from '../../components/OrderSheet';
import { recognizeMenuFromImages } from '../../services/imageRecognition';

function MenuPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [currency, setCurrency] = useState('¥');
  const [cart, setCart] = useState([]);
  const [selectedDishes, setSelectedDishes] = useState({});
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [stickyCategory, setStickyCategory] = useState(null);
  const [stickyTitle, setStickyTitle] = useState(null);
  const [showOrderSheet, setShowOrderSheet] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDish, setSelectedDish] = useState(null);
  const [showDishDetail, setShowDishDetail] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [menuData, setMenuData] = useState(null);
  const [lastTranslation, setLastTranslation] = useState(null);
  const [cartAnimation, setCartAnimation] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [orderData, setOrderData] = useState(null);

  // 处理从路由传递来的菜单数据
  useEffect(() => {
    const initializeMenuData = async () => {
      try {
        let data = location.state?.menuData;
        const isStreaming = location.state?.isStreaming;
        
        console.log('初始化菜单数据:', { data, isStreaming });
        
        // 如果没有路由数据，尝试从localStorage恢复
        if (!data) {
          const savedData = localStorage.getItem('lastMenuData');
          if (savedData) {
            data = JSON.parse(savedData);
            console.log('从localStorage恢复的数据:', data);
          }
        }

        // 如果没有数据，显示上传界面
        if (!data || !data.menu) {
          console.log('无有效菜单数据，显示上传界面');
          setShowMenu(false);
          setIsInitialized(true);
          return;
        }

        // 处理菜单数据
        if (data.menu) {
          console.log('处理菜单数据:', data.menu);
          setMenuData(data);
          
          // 设置货币
          if (data.menu.menu_currency) {
            setCurrency(data.menu.menu_currency);
          }

          // 如果有分类数据，处理分类
          if (data.menu.categories && data.menu.categories.length > 0) {
            const menuCategories = data.menu.categories.map(category => ({
              name: String(category.translation_category_name || '未命名分类'),
              translation_name: category.raw_category_name,
              description: category.translation_description ? {
                brief: category.translation_description.brief || '',
                detail: category.translation_description.detail || ''
              } : null,
              dishes: (category.dishes || []).map(dish => ({
                rawName: String(dish.raw_name || ''),
                rawPrice: Number(dish.raw_price || 0),
                name: String(dish.translation_name || ''),
                price: Number(dish.translation_price || 0),
                description: {
                  brief: String(dish.translation_description?.brief || ''),
                  detail: String(dish.translation_description?.detail || '')
                },
                cookingMethod: String(dish.translation_cooking_method || ''),
                ingredients: Array.isArray(dish.translation_ingredients) ? dish.translation_ingredients.map(String) : [],
                taste: Array.isArray(dish.translation_taste) ? dish.translation_taste.map(String) : [],
                allergens: Array.isArray(dish.translation_allergens) ? dish.translation_allergens.map(String) : [],
                hasAddon: Boolean(dish.dish_has_addon),
                hasOptions: Boolean(dish.dish_has_options),
                options: Array.isArray(dish.dish_options) ? dish.dish_options : [],
                addons: Array.isArray(dish.dish_addon) ? dish.dish_addon : []
              }))
            }));

            console.log('处理后的分类数据:', menuCategories);
            setCategories(menuCategories);
          }

          // 只要有菜单数据就显示菜单页面
          setShowMenu(true);
          console.log('设置菜单数据完成，showMenu:', true);
          
          // 如果不是流式数据，则保存到localStorage
          if (!isStreaming) {
            localStorage.setItem('lastMenuData', JSON.stringify(data));
          }
        }
      } catch (error) {
        console.error('Error processing menu data:', error);
        setErrorMessage('处理菜单数据时出错，请重试');
        setShowError(true);
        setShowMenu(false);
      } finally {
        setIsInitialized(true);
      }
    };

    initializeMenuData();
  }, [location.state]);

  // 监听流式数据更新
  useEffect(() => {
    if (location.state?.isStreaming) {
      console.log('设置流式数据更新监听:', menuData);
      
      const handleStreamingUpdate = (data) => {
        console.log('收到流式数据更新:', data);
        
        if (data?.menu) {
          // 更新菜单数据
          setMenuData(data);
          
          // 更新货币
          if (data.menu.menu_currency) {
            setCurrency(data.menu.menu_currency);
          }

          // 更新分类数据
          if (data.menu.categories && data.menu.categories.length > 0) {
            const menuCategories = data.menu.categories.map(category => ({
              name: String(category.translation_category_name || '未命名分类'),
              translation_name: category.raw_category_name,
              description: category.translation_description ? {
                brief: category.translation_description.brief || '',
                detail: category.translation_description.detail || ''
              } : null,
              dishes: (category.dishes || []).map(dish => ({
                rawName: String(dish.raw_name || ''),
                rawPrice: Number(dish.raw_price || 0),
                name: String(dish.translation_name || ''),
                price: Number(dish.translation_price || 0),
                description: {
                  brief: String(dish.translation_description?.brief || ''),
                  detail: String(dish.translation_description?.detail || '')
                },
                cookingMethod: String(dish.translation_cooking_method || ''),
                ingredients: Array.isArray(dish.translation_ingredients) ? dish.translation_ingredients.map(String) : [],
                taste: Array.isArray(dish.translation_taste) ? dish.translation_taste.map(String) : [],
                allergens: Array.isArray(dish.translation_allergens) ? dish.translation_allergens.map(String) : [],
                hasAddon: Boolean(dish.dish_has_addon),
                hasOptions: Boolean(dish.dish_has_options),
                options: Array.isArray(dish.dish_options) ? dish.dish_options : [],
                addons: Array.isArray(dish.dish_addon) ? dish.dish_addon : []
              }))
            }));

            console.log('更新分类数据:', menuCategories);
            setCategories(menuCategories);
          }
        }
      };

      // 设置回调函数
      if (typeof recognizeMenuFromImages.callback === 'function') {
        recognizeMenuFromImages.callback = handleStreamingUpdate;
      }
    }
  }, [location.state?.isStreaming]);

  const handleAddToCart = (dish, categoryName, selectedOption = null, selectedAddon = null, startPosition = null, quantity = 1) => {
    if (dish.hasOptions && !selectedOption) {
      setErrorMessage('请选择规格');
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
        setErrorMessage('');
      }, 3000);
      return;
    }

    const cartIcon = document.querySelector('.cart-icon');
    if (startPosition && cartIcon) {
      const cartRect = cartIcon.getBoundingClientRect();
      setCartAnimation({
        startPosition,
        endPosition: {
          x: cartRect.left + cartRect.width / 2 - 16,
          y: cartRect.top + cartRect.height / 2 - 16
        }
      });
    }

    const cartItem = {
      id: Date.now(),
      dish,
      categoryName,
      selectedOption,
      selectedAddon,
      quantity: quantity
    };
    setCart([...cart, cartItem]);
  };

  const handleRemoveFromCart = (id) => {
    setCart(cart.filter(item => item.id !== id));
  };

  const handleUpdateQuantity = (id, change) => {
    setCart(cart.map(item => {
      if (item.id === id) {
        const newQuantity = item.quantity + change;
        if (newQuantity > 0) {
          return { ...item, quantity: newQuantity };
        }
      }
      return item;
    }));
  };

  const calculateItemPrice = (item) => {
    let price = Number(item.dish.price);
    
    if (item.selectedOption && item.selectedOption.translation_price) {
      price = Number(item.selectedOption.translation_price);
    }
    
    if (item.selectedAddon && item.selectedAddon.translation_price) {
      price += Number(item.selectedAddon.translation_price);
    }
    
    return (price * item.quantity).toFixed(2);
  };

  const calculateTotalPrice = () => {
    return cart.reduce((total, item) => {
      return (Number(total) + Number(calculateItemPrice(item))).toFixed(2);
    }, 0);
  };

  const calculateTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const handleImagesUploaded = async (images) => {
    setIsLoading(true);
    let isFirstResponse = true;
    try {
      const result = await recognizeMenuFromImages(images, (intermediateResult) => {
        // 处理中间结果，更新UI
        if (intermediateResult?.menu) {
          setMenuData(intermediateResult);
          
          if (intermediateResult.menu.menu_currency) {
            setCurrency(intermediateResult.menu.menu_currency);
          }

          if (intermediateResult.menu.categories) {
            const menuCategories = intermediateResult.menu.categories.map(category => ({
              name: String(category.translation_category_name || '未命名分类'),
              translation_name: category.raw_category_name,
              description: category.translation_description ? {
                brief: category.translation_description.brief || '',
                detail: category.translation_description.detail || ''
              } : null,
              dishes: (category.dishes || []).map(dish => ({
                rawName: String(dish.raw_name || ''),
                rawPrice: Number(dish.raw_price || 0),
                name: String(dish.translation_name || ''),
                price: Number(dish.translation_price || 0),
                description: {
                  brief: String(dish.translation_description?.brief || ''),
                  detail: String(dish.translation_description?.detail || '')
                },
                cookingMethod: String(dish.translation_cooking_method || ''),
                ingredients: Array.isArray(dish.translation_ingredients) ? dish.translation_ingredients.map(String) : [],
                taste: Array.isArray(dish.translation_taste) ? dish.translation_taste.map(String) : [],
                allergens: Array.isArray(dish.translation_allergens) ? dish.translation_allergens.map(String) : [],
                hasAddon: Boolean(dish.dish_has_addon),
                hasOptions: Boolean(dish.dish_has_options),
                options: Array.isArray(dish.dish_options) ? dish.dish_options : [],
                addons: Array.isArray(dish.dish_addon) ? dish.dish_addon : []
              }))
            }));

            setCategories(menuCategories);
            
            // 第一次收到数据时，显示菜单并保存到localStorage
            if (isFirstResponse) {
              setShowMenu(true);
              localStorage.setItem('lastMenuData', JSON.stringify(intermediateResult));
              isFirstResponse = false;
              setIsLoading(false); // 第一次数据加载完成后关闭loading
            }
          }
        }
      });
      
      if (result) {
        // 最终结果处理
        setMenuData(result);
        localStorage.setItem('lastMenuData', JSON.stringify(result));
      } else {
        throw new Error('识别失败，请重试');
      }
    } catch (error) {
      console.error('Menu recognition error:', error);
      setErrorMessage(error.message || '识别失败，请重试');
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
        setErrorMessage('');
      }, 3000);
      setIsLoading(false);
    }
  };

  const handleGenerateOrderSheet = () => {
    try {
      // 生成订单数据
      const orderData = {
        orderTime: new Date(),
        items: cart.map(item => ({
          name: item.dish.name,
          rawName: item.dish.rawName,
          quantity: item.quantity,
          price: item.dish.price,
          rawPrice: item.dish.rawPrice,
          categoryName: item.categoryName,
          selectedOption: item.selectedOption ? {
            ...item.selectedOption,
            raw_title: item.selectedOption.raw_title || item.selectedOption.translation_title
          } : null,
          selectedAddon: item.selectedAddon ? {
            ...item.selectedAddon,
            raw_title: item.selectedAddon.raw_title || item.selectedAddon.translation_title
          } : null
        })),
        totalAmount: calculateTotalPrice(),
        totalAmountRaw: cart.reduce((total, item) => {
          return total + (item.dish.rawPrice * item.quantity);
        }, 0).toFixed(2),
        currency
      };

      setOrderData(orderData);
      setShowOrderSheet(true);
      setShowCart(false);
    } catch (error) {
      console.error('生成点菜单失败:', error);
      setErrorMessage('生成点菜单失败，请重试');
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
        setErrorMessage('');
      }, 3000);
    }
  };

  const handlePrintOrderSheet = () => {
    window.print();
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50">
      <PageHeader 
        title={showMenu ? "菜单详情" : "菜单识别"} 
        subtitle={showMenu ? (
          isLoading ? "正在优化识别结果..." : 
          categories.length > 0 ? `共 ${categories.length} 个分类` :
          menuData?.menu?.menu_name ? menuData.menu.menu_name :
          "正在识别菜单..."
        ) : "上传菜单图片开始识别"}
      />
      
      <div className="flex-1">
        {isLoading && !showMenu && <LoadingOverlay />}
        
        {!showMenu ? (
          <div className="flex-1 flex flex-col items-center justify-center">
            <ImageUploader onImagesUploaded={handleImagesUploaded} />
          </div>
        ) : (
          <div className="flex-1 relative">
            {isLoading && (
              <div className="absolute top-0 left-0 right-0 bg-indigo-600 text-white text-center text-sm py-1">
                正在优化识别结果，页面会自动更新...
              </div>
            )}
            {categories.length > 0 ? (
              <MenuList
                categories={categories}
                onAddToCart={handleAddToCart}
                currency={currency}
                menuData={menuData}
                setSelectedDish={setSelectedDish}
                setShowDishDetail={setShowDishDetail}
                pageTransition={false}
                onBackToHome={() => {
                  localStorage.removeItem('lastMenuData');
                  setShowMenu(false);
                  setCategories([]);
                  setMenuData(null);
                  setIsInitialized(false);
                  navigate('/', { replace: true });
                }}
              />
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-4">
                <div className="animate-pulse">
                  <svg className="w-12 h-12 text-indigo-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <p className="text-gray-600 text-center mb-2">
                  {menuData?.menu?.menu_name ? (
                    <>
                      <span className="font-medium block text-lg">{menuData.menu.menu_name}</span>
                      <span className="text-sm">正在识别菜品，请稍候...</span>
                    </>
                  ) : (
                    "正在识别菜单内容..."
                  )}
                </p>
              </div>
            )}
          </div>
        )}

        {showDishDetail && selectedDish && (
          <DishDetail
            dish={selectedDish}
            onClose={() => setShowDishDetail(false)}
            onAddToCart={handleAddToCart}
          />
        )}

        {showCart && (
          <CartDetail
            cart={cart}
            onClose={() => setShowCart(false)}
            onRemoveFromCart={handleRemoveFromCart}
            onUpdateQuantity={handleUpdateQuantity}
            calculateItemPrice={calculateItemPrice}
            calculateTotalPrice={calculateTotalPrice}
            currency={currency}
            onGenerateOrderSheet={handleGenerateOrderSheet}
          />
        )}

        {showOrderSheet && orderData && (
          <OrderSheet
            orderData={orderData}
            onClose={() => setShowOrderSheet(false)}
            onPrint={handlePrintOrderSheet}
          />
        )}

        {cartAnimation && (
          <AddToCartAnimation
            startPosition={cartAnimation.startPosition}
            endPosition={cartAnimation.endPosition}
            onComplete={() => setCartAnimation(null)}
          />
        )}

        {/* 移动端底部固定购物车 */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 backdrop-blur-lg bg-white/70 shadow-lg border-t border-gray-200/30 z-20">
          <div 
            className="max-w-6xl mx-auto px-4 py-3 cursor-pointer"
            onClick={() => setShowCart(true)}
          >
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs text-gray-600">
                  {cart.length > 0 ? `已选 ${calculateTotalItems()} 道菜` : '购物车为空'}
                </p>
                <p className="text-base font-semibold text-indigo-600">
                  ¥{calculateTotalPrice()}
                </p>
              </div>
              <div className="flex items-center">
                <div className="relative">
                  <svg className="w-6 h-6 text-indigo-600 cart-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  {cart.length > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {calculateTotalItems()}
                    </span>
                  )}
                </div>
                <span className="text-indigo-600 text-sm font-medium ml-2">查看购物车</span>
              </div>
            </div>
          </div>
        </div>

        {showError && (
          <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-4 py-2 rounded-lg">
            {errorMessage}
          </div>
        )}
      </div>
    </div>
  );
}

export default MenuPage; 